<template>
  <div class="traffic-page">
    <header class="traffic-header">
      <div class="traffic-brand">
        <el-button type="text" icon="el-icon-arrow-left" class="traffic-back" @click="$router.push('/HelloWorld')">主页</el-button>
        <div class="traffic-tab"><i class="el-icon-data-analysis"></i> 流量主数据</div>
      </div>
      <div class="traffic-user">
        <span class="traffic-version">ECPM 数据后台</span>
        <span><i class="el-icon-user"></i> {{ accountLabel }}</span>
      </div>
    </header>

    <main class="traffic-content">
      <div class="traffic-title-row">
        <div>
          <div class="traffic-kicker">数据分析</div>
          <h1>流量主数据</h1>
          <p>按游戏、宿主、操作系统与渠道分析广告收益，支持趋势、维度对比及明细导出。</p>
        </div>
        <el-button type="text" icon="el-icon-switch-button" @click="quit">退出</el-button>
      </div>

      <div class="traffic-layout">
        <aside class="game-sidebar">
          <el-input
            v-model="gameKeyword"
            prefix-icon="el-icon-search"
            clearable
            placeholder="搜索游戏名称"
            class="game-search-input"
          ></el-input>
          <div v-if="gameGroups.length === 0" class="sidebar-empty">暂无可查看游戏</div>
          <div v-for="group in gameGroups" :key="group.username" class="game-group">
            <div class="game-group-title">
              <div class="game-group-account">
                <span class="game-group-avatar">{{ accountInitial(group.username) }}</span>
                <div>
                  <strong>{{ group.username }}</strong>
                  <small>{{ group.games.length }} 个游戏</small>
                </div>
              </div>
              <span class="game-group-hint">点击游戏卡片查看详情</span>
            </div>
            <article
              v-for="game in group.games"
              :key="game.gameId || game.gameName"
              :class="['sidebar-game-card', { active: selectedGame === game.gameName }]"
              @click="selectGame(game.gameName)"
            >
              <div class="sidebar-game-head">
                <div class="sidebar-game-icon"><img src="/Logo.png" alt="游戏图标" /></div>
                <div class="sidebar-game-title">
                  <strong :title="game.gameName">{{ game.gameName }}</strong>
                  <small>游戏 ID {{ game.gameId || '--' }}</small>
                </div>
                <i class="el-icon-arrow-right sidebar-game-arrow"></i>
              </div>
              <div class="sidebar-metric-grid">
                <div><span>广告 PV</span><strong>{{ sidebarMetric(game, 'pv') }}</strong></div>
                <div><span>用户数</span><strong>{{ sidebarMetric(game, 'userCount') }}</strong></div>
                <div><span>ECPM</span><strong>{{ sidebarMetric(game, 'ecpm') }}</strong></div>
                <div><span>分成前收益</span><strong class="sidebar-pre-income">{{ sidebarMetric(game, 'preShareIncome') }}</strong></div>
                <div class="sidebar-post-metric"><span>分成后收益</span><strong>{{ sidebarMetric(game, 'postShareIncome') }}</strong></div>
              </div>
              <div class="sidebar-game-foot">
                <span><i class="el-icon-info"></i> {{ sidebarState(game) }}</span>
                <b>查看明细 <i class="el-icon-arrow-right"></i></b>
              </div>
            </article>
          </div>
        </aside>

        <section class="traffic-main">
          <div class="filter-panel">
            <div class="filter-item filter-date">
              <label>日期区间</label>
              <el-date-picker
                v-model="dateRange"
                type="daterange"
                value-format="yyyy-MM-dd"
                format="yyyy-MM-dd"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                :clearable="false"
              ></el-date-picker>
            </div>
            <div class="filter-item">
              <label>广告类型</label>
              <el-select v-model="adTypeFilter" placeholder="全部" filterable>
                <el-option v-for="item in adTypeOptions" :key="item" :label="item" :value="item"></el-option>
              </el-select>
            </div>
            <div class="filter-item">
              <label>归因渠道</label>
              <el-select v-model="channelFilter" placeholder="全部" filterable>
                <el-option v-for="item in channelOptions" :key="item" :label="item" :value="item"></el-option>
              </el-select>
            </div>
            <div class="filter-item">
              <label>宿主 App</label>
              <el-select v-model="hostAppFilter" placeholder="全部" filterable>
                <el-option v-for="item in hostAppOptions" :key="item" :label="item" :value="item"></el-option>
              </el-select>
            </div>
            <div class="filter-item filter-os">
              <label>操作系统</label>
              <el-select v-model="osFilter" placeholder="全部" filterable>
                <el-option v-for="item in osOptions" :key="item" :label="item" :value="item"></el-option>
              </el-select>
            </div>
            <div class="filter-actions">
              <el-button type="primary" icon="el-icon-search" :loading="isLoading" @click="queryTraffic">查询并同步</el-button>
              <el-button type="success" icon="el-icon-time" :loading="realtimeLoading" @click="queryMiniAppRealtime">小程序实时收益</el-button>
              <el-button icon="el-icon-download" :disabled="!filteredRecords.length" @click="exportDetailCsv">导出明细 CSV</el-button>
            </div>
            <div class="filter-compare">
              <label>对比维度</label>
              <el-select v-model="compareDimension">
                <el-option label="渠道" value="channel"></el-option>
                <el-option label="宿主 App" value="hostApp"></el-option>
                <el-option label="操作系统" value="os"></el-option>
                <el-option label="广告类型" value="adType"></el-option>
              </el-select>
              <el-button icon="el-icon-s-data" :disabled="!filteredRecords.length" @click="startCompare">开始对比</el-button>
            </div>
          </div>

          <section v-if="realtimeData" class="data-panel realtime-panel">
            <div class="panel-title"><i></i>小程序准实时收益 <span>约 10～15 分钟延迟</span></div>
            <div class="realtime-cards">
              <div class="realtime-card"><span>准实时广告消耗</span><strong>¥{{ formatRealtimeCost(realtimeData.totalCostYuan) }}</strong></div>
              <div class="realtime-card"><span>广告请求次数</span><strong>{{ formatInteger(realtimeData.totalIpu) }}</strong></div>
              <div class="realtime-card"><span>ECPM</span><strong>后台数据接口未返回</strong></div>
              <div class="realtime-card"><span>最近事件</span><strong>{{ realtimeData.latestEventTime || "--" }}</strong></div>
            </div>
            <p class="summary-note">实时接口返回的是 5 分钟聚合的 cost 估算值，最终收入以抖音结算数据为准。当前接口未返回曝光 PV，因此不计算 ECPM。</p>
            <el-table :data="realtimeData.records" stripe border height="250" class="traffic-table" empty-text="暂无实时数据">
              <el-table-column prop="eventTime" label="事件时间" min-width="170"></el-table-column>
              <el-table-column prop="adType" label="广告类型" min-width="120"></el-table-column>
              <el-table-column prop="ipu" label="请求次数" width="110"></el-table-column>
              <el-table-column label="广告消耗(元)" width="150"><template slot-scope="scope">{{ formatRealtimeCost(scope.row.costYuan) }}</template></el-table-column>
            </el-table>
          </section>

          <div v-if="dateRangeWarning" class="traffic-warning"><i class="el-icon-warning-outline"></i>{{ dateRangeWarning }}</div>

          <div class="summary-grid">
            <div v-for="card in summaryCards" :key="card.label" class="summary-card">
              <span>{{ card.label }}</span>
              <strong>{{ card.value }}</strong>
            </div>
          </div>
          <p class="summary-note">金额单位为元；分成前、分成后收入及明细 ECPM 直接读取抖音广告收入接口。未返回的数据标为待同步，汇总仅对官方金额求和。</p>

          <section class="data-panel trend-panel">
            <div class="panel-title"><i></i>收益趋势</div>
            <div v-if="trendData.length" class="trend-chart">
              <div v-for="item in trendData" :key="item.date" class="trend-column">
                <div class="trend-value">¥{{ item.income.toFixed(2) }}</div>
                <div class="trend-bar-wrap"><div class="trend-bar" :style="{ height: `${item.percent}%` }"></div></div>
                <span>{{ item.date.slice(5) }}</span>
              </div>
            </div>
            <div v-else class="panel-empty">暂无趋势数据，请先查询</div>
          </section>

          <section class="data-panel detail-panel">
            <div class="panel-title"><i></i>数据明细 <span>{{ filteredRecords.length }} 条</span></div>
            <el-table :data="filteredRecords" stripe border height="360" class="traffic-table" empty-text="暂无数据，请先查询">
              <el-table-column prop="date" label="日期" width="110"></el-table-column>
              <el-table-column prop="host" label="宿主" min-width="110"></el-table-column>
              <el-table-column prop="os" label="操作系统" min-width="100"></el-table-column>
              <el-table-column prop="channel" label="渠道" min-width="110"></el-table-column>
              <el-table-column prop="adType" label="广告类型" min-width="100"></el-table-column>
              <el-table-column prop="requestPv" label="请求PV" width="90"></el-table-column>
              <el-table-column prop="displayPv" label="展示PV" width="90"></el-table-column>
              <el-table-column prop="clickPv" label="点击PV" width="90"></el-table-column>
              <el-table-column label="点击率" width="90">
                <template slot-scope="scope">{{ formatPercent(scope.row.clickRate) }}</template>
              </el-table-column>
              <el-table-column label="ECPM" width="100">
                <template slot-scope="scope">{{ formatIncome(scope.row.ecpm) }}</template>
              </el-table-column>
              <el-table-column label="分成前收入(元)" width="130">
                <template slot-scope="scope">{{ formatIncome(scope.row.preIncome) }}</template>
              </el-table-column>
              <el-table-column label="分成后收入(元)" width="130">
                <template slot-scope="scope">{{ formatIncome(scope.row.postIncome) }}</template>
              </el-table-column>
            </el-table>
          </section>

          <section class="data-panel compare-panel">
            <div class="panel-title"><i></i>收益对比</div>
            <p class="compare-description">按所选维度汇总当前查询结果。对比项会按照分成后收入计算收入占比。</p>
            <el-button size="small" icon="el-icon-download" :disabled="!comparisonRows.length" @click="exportCompareCsv">导出对比 CSV</el-button>
            <el-table v-if="comparisonRows.length" :data="comparisonRows" stripe border class="traffic-table compare-table">
              <el-table-column prop="name" label="对比项" min-width="150"></el-table-column>
              <el-table-column prop="status" label="查询状态" width="110"></el-table-column>
              <el-table-column prop="displayPv" label="展示PV" width="110"></el-table-column>
              <el-table-column prop="clickPv" label="点击PV" width="110"></el-table-column>
              <el-table-column label="点击率" width="110"><template slot-scope="scope">{{ formatPercent(scope.row.clickRate) }}</template></el-table-column>
              <el-table-column label="分成前收入(元)" width="150"><template slot-scope="scope">{{ formatIncome(scope.row.preIncome) }}</template></el-table-column>
              <el-table-column label="分成后收入(元)" width="150"><template slot-scope="scope">{{ formatIncome(scope.row.postIncome) }}</template></el-table-column>
              <el-table-column label="ECPM（分成后）" width="150"><template slot-scope="scope">{{ formatIncome(scope.row.ecpm) }}</template></el-table-column>
              <el-table-column label="收入占比" width="110"><template slot-scope="scope">{{ formatPercent(scope.row.share) }}</template></el-table-column>
            </el-table>
            <div v-else class="panel-empty">暂无对比，请点击“开始对比”</div>
          </section>
        </section>
      </div>
    </main>
  </div>
</template>

<script>
import http from "../api/http";
import { normalizeOfficialIncome, sumOfficial } from "../utils/officialIncome";

function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}


export default {
  name: "TrafficMasterData",
  data() {
    const today = formatLocalDate(new Date());
    return {
      role: localStorage.getItem("role"),
      games: [],
      sidebarSummaries: {},
      gameKeyword: "",
      selectedGame: "",
      dateRange: [today, today],
      appliedDateRange: [today, today],
      adTypeFilter: "全部",
      channelFilter: "全部",
      hostAppFilter: "全部",
      osFilter: "全部",
      compareDimension: "channel",
      records: [],
      comparisonRows: [],
      isLoading: false,
      dateRangeWarning: "",
      missingIncomeDates: [],
      queryVersion: 0,
      sidebarVersion: 0,
      sidebarRefreshTimer: null,
      realtimeData: null,
      realtimeLoading: false,
      realtimeTimer: null
    };
  },
  computed: {
    accountLabel() {
      if (this.role === "0") return "管理员";
      const selected = this.games.find(item => item.gameName === this.selectedGame);
      return selected && selected.username ? selected.username : "当前账号";
    },
    gameGroups() {
      const keyword = this.gameKeyword.trim().toLowerCase();
      const groups = {};
      this.games.forEach(game => {
        const name = game.gameName || "";
        if (keyword && !name.toLowerCase().includes(keyword)) return;
        const username = game.username || "当前账号";
        if (!groups[username]) groups[username] = [];
        groups[username].push(game);
      });
      return Object.keys(groups).sort().map(username => ({
        username,
        games: groups[username].sort((a, b) => (a.gameName || "").localeCompare(b.gameName || ""))
      }));
    },
    accountGameOptions() {
      return [...new Set(this.games.map(item => item.gameName).filter(Boolean))];
    },
    filteredRecords() {
      return this.records.filter(record =>
        (this.adTypeFilter === "全部" || record.adType === this.adTypeFilter) &&
        (this.channelFilter === "全部" || record.channel === this.channelFilter) &&
        (this.hostAppFilter === "全部" || record.hostApp === this.hostAppFilter) &&
        (this.osFilter === "全部" || record.os === this.osFilter)
      );
    },
    adTypeOptions() {
      return [...new Set(["全部", ...this.records.map(item => item.adType).filter(Boolean)])];
    },
    channelOptions() {
      return [...new Set(["全部", ...this.records.map(item => item.channel).filter(Boolean)])];
    },
    hostAppOptions() {
      return [...new Set(["全部", ...this.records.map(item => item.hostApp).filter(Boolean)])];
    },
    osOptions() {
      return [...new Set(["全部", ...this.records.map(item => item.os).filter(Boolean)])];
    },
    summary() {
      const rows = this.filteredRecords;
      return {
        requestPv: sumOfficial(rows, "requestPv"), displayPv: sumOfficial(rows, "displayPv"),
        clickPv: sumOfficial(rows, "clickPv"), preIncome: sumOfficial(rows, "preIncome"),
        postIncome: sumOfficial(rows, "postIncome")
      };
    },
    summaryCards() {
      const item = this.summary;
      const partial = this.missingIncomeDates.length > 0;
      const money = value => value === null ? "待同步" : '¥' + this.formatNumber(value);
      return [
        { label: "总请求PV", value: this.formatInteger(item.requestPv) },
        { label: "总展示PV", value: this.formatInteger(item.displayPv) },
        { label: "总点击PV", value: this.formatInteger(item.clickPv) },
        { label: "平均点击率", value: this.formatPercent(item.displayPv === null || item.clickPv === null ? null : item.displayPv ? item.clickPv / item.displayPv : 0) },
        { label: "分成后 ECPM", value: this.formatIncome(item.displayPv === null || item.postIncome === null ? null : item.displayPv ? item.postIncome / item.displayPv * 1000 : 0) },
        { label: partial ? "已返回分成前收入" : "分成前总收入", value: money(item.preIncome) },
        { label: partial ? "已返回分成后收入" : "分成后总收入", value: money(item.postIncome) }
      ];
    },
    trendData() {
      const map = {};
      this.filteredRecords.forEach(item => {
        if (item.preIncome === null) return;
        if (!map[item.date]) map[item.date] = 0;
        map[item.date] += item.preIncome;
      });
      const values = Object.keys(map).sort().map(date => ({ date, income: map[date] }));
      const max = Math.max(...values.map(item => item.income), 0);
      return values.map(item => ({ ...item, percent: max ? Math.max(8, item.income / max * 100) : 8 }));
    }
  },
  watch: {
    dateRange() {
      this.comparisonRows = [];
    },
    selectedGame() {
      this.comparisonRows = [];
    },
    adTypeFilter() { this.comparisonRows = []; },
    channelFilter() { this.comparisonRows = []; },
    hostAppFilter() { this.comparisonRows = []; },
    osFilter() { this.comparisonRows = []; }
  },
  methods: {
    authConfig() {
      return { headers: { token: `${localStorage.getItem("token")}` } };
    },
    fetchGames() {
      http.get("/api/userGames/getUserGames", this.authConfig()).then(res => {
        if (!res.data || res.data.code !== 1) {
          this.$message.error(res.data?.msg || "获取游戏列表失败");
          return;
        }
        this.games = Array.isArray(res.data.data) ? res.data.data : [];
        if (!this.selectedGame || !this.accountGameOptions.includes(this.selectedGame)) {
          this.selectedGame = this.accountGameOptions[0] || "";
        }
        if (this.selectedGame) this.queryTraffic();
      }).catch(() => this.$message.error("获取游戏列表失败"));
    },
    selectGame(gameName) {
      this.selectedGame = gameName;
      this.realtimeData = null;
      this.queryTraffic();
    },
    loadSidebarSummaries() {
      const names = [...new Set(this.games.map(game => game.gameName).filter(Boolean))];
      if (!names.length) return;
      const [startDate, endDate] = this.appliedDateRange;
      const version = ++this.sidebarVersion;
      return http.post("/api/ecpm/rangeSummaries", names, {
        ...this.authConfig(), params: { startDate, endDate }
      }).then(response => {
        if (version !== this.sidebarVersion) return;
        if (!response.data || response.data.code !== 1 || !Array.isArray(response.data.data)) return;
        const summaries = {};
        response.data.data.forEach(summary => { summaries[summary.gameName] = summary; });
        this.sidebarSummaries = summaries;
      }).catch(() => {});
    },
    sidebarState(game) {
      const summary = this.sidebarSummaries[game.gameName];
      return summary && summary.postShareComplete ? "分成后收入已从抖音同步" : "分成后收入待抖音返回";
    },
    accountInitial(name) {
      return (name || "账").slice(0, 1).toUpperCase();
    },
    sidebarMetric(game, key) {
      const summary = this.sidebarSummaries[game.gameName];
      if (key === "postShareIncome" && (!summary || !summary.postShareComplete)) return "待同步";
      if (!summary || (summary.cached === false && key !== "postShareIncome")) return "--";
      const raw = key === "preShareIncome" && (summary[key] === undefined || summary[key] === null)
        ? summary.income : summary[key];
      if (raw === undefined || raw === null) return "--";
      if (["ecpm", "preShareIncome", "postShareIncome"].includes(key)) {
        const value = Number(raw);
        return Number.isFinite(value) ? value.toFixed(2) : "0.00";
      }
      return raw;
    },
    listDates() {
      const start = new Date(`${this.dateRange[0]}T00:00:00`);
      const end = new Date(`${this.dateRange[1]}T00:00:00`);
      const dates = [];
      for (let cursor = start; cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
        dates.push(formatLocalDate(cursor));
      }
      return dates;
    },
    async queryTraffic() {
      if (!this.selectedGame) { this.$message.warning("请先选择游戏"); return; }
      if (!Array.isArray(this.dateRange) || this.dateRange.length !== 2) { this.$message.warning("请选择日期区间"); return; }
      const dates = this.listDates();
      if (!dates.length || dates.length > 31 || dates[dates.length - 1] > formatLocalDate(new Date())) {
        this.$message.warning("请选择不超过今天的 1 至 31 天日期区间"); return;
      }
      const version = ++this.queryVersion;
      const gameName = this.selectedGame;
      const [startDate, endDate] = this.dateRange;
      const game = this.games.find(item => item.gameName === gameName);
      const account = game && game.username ? game.username : this.accountLabel;
      this.isLoading = true;
      this.records = [];
      this.comparisonRows = [];
      this.dateRangeWarning = "";
      this.missingIncomeDates = [];
      this.appliedDateRange = [startDate, endDate];
      this.sidebarSummaries = {};
      this.loadSidebarSummaries();
      try {
        let response = await http.post("/api/ecpm/adData", { gameName, startDate, endDate }, this.authConfig()).catch(() => null);
        if (version !== this.queryVersion) return;
        let warning = "";
        if (!response || !response.data || response.data.code !== 1) {
          warning = (response && response.data && response.data.msg || "抖音收入同步失败") + "；如有历史官方记录，将保留显示。";
          response = await http.get("/api/ecpm/adIncome", { ...this.authConfig(), params: { gameName, startDate, endDate } }).catch(() => null);
        }
        if (version !== this.queryVersion) return;
        const data = response && response.data && response.data.code === 1 ? response.data.data : null;
        if (!data || data.source !== "douyin_official" || !Array.isArray(data.items)) {
          this.dateRangeWarning = warning || "尚未取得抖音广告收入，请稍后重新查询。";
          return;
        }
        this.records = data.items.map(item => normalizeOfficialIncome(item, account));
        this.missingIncomeDates = data.missingDates || [];
        const missing = this.missingIncomeDates.length ? '抖音暂未返回 ' + this.missingIncomeDates.join('、') + ' 的收入；仅显示已返回日期。' : "";
        this.dateRangeWarning = [warning, missing].filter(Boolean).join(" ");
        if (!this.records.length && !this.dateRangeWarning) this.dateRangeWarning = "抖音暂无广告收入记录，金额待同步。";
        this.resetFilterOptions();
        this.loadSidebarSummaries();
      } finally {
        if (version === this.queryVersion) this.isLoading = false;
      }
    },
    realtimeRange() {
      const end = new Date();
      const start = new Date(end.getTime() - 60 * 60 * 1000);
      const format = value => `${formatLocalDate(value)} ${String(value.getHours()).padStart(2, "0")}:${String(value.getMinutes()).padStart(2, "0")}`;
      return [format(start), format(end)];
    },
    async queryMiniAppRealtime(showMessage = true) {
      if (!this.selectedGame) { this.$message.warning("请先选择小程序"); return; }
      this.realtimeLoading = true;
      const [startDate, endDate] = this.realtimeRange();
      try {
        const response = await http.get("/api/ecpm/miniAppRealtime", {
          ...this.authConfig(), params: { gameName: this.selectedGame, startDate, endDate }
        });
        if (!response.data || response.data.code !== 1) {
          this.realtimeData = null;
          if (showMessage) this.$message.warning(response.data && response.data.msg || "该应用暂未配置为小程序");
          return;
        }
        this.realtimeData = response.data.data;
      } catch (error) {
        this.realtimeData = null;
        if (showMessage) this.$message.error("小程序实时收益查询失败");
      } finally {
        this.realtimeLoading = false;
      }
    },
    resetFilterOptions() {
      const reset = (key, options) => {
        if (!options.includes(this[key])) this[key] = "全部";
      };
      this.$nextTick(() => {
        reset("adTypeFilter", this.adTypeOptions);
        reset("channelFilter", this.channelOptions);
        reset("hostAppFilter", this.hostAppOptions);
        reset("osFilter", this.osOptions);
      });
    },
    startCompare() {
      const groups = {};
      this.filteredRecords.forEach(record => {
        const name = record[this.compareDimension] || "全部";
        if (!groups[name]) groups[name] = [];
        groups[name].push(record);
      });
      const totalPost = this.summary.postIncome;
      this.comparisonRows = Object.keys(groups).sort().map(name => {
        const records = groups[name];
        const displayPv = sumOfficial(records, "displayPv"), clickPv = sumOfficial(records, "clickPv");
        const postIncome = sumOfficial(records, "postIncome");
        return {
          name, status: postIncome === null ? "待同步" : "官方已返回", displayPv, clickPv,
          preIncome: sumOfficial(records, "preIncome"), postIncome,
          clickRate: displayPv === null || clickPv === null ? null : displayPv ? clickPv / displayPv : 0,
          ecpm: postIncome === null || displayPv === null ? null : displayPv ? postIncome / displayPv * 1000 : 0,
          share: postIncome === null || totalPost === null ? null : totalPost ? postIncome / totalPost : 0
        };
      });
    },
    exportDetailCsv() {
      const rows = this.filteredRecords.map(item => ({
        数据来源: "抖音官方", 日期: item.date, 宿主: item.host, 操作系统: item.os, 渠道: item.channel, 广告类型: item.adType,
        请求PV: item.requestPv, 展示PV: item.displayPv, 点击PV: item.clickPv, 点击率: this.formatPercent(item.clickRate),
        ECPM: this.formatIncome(item.ecpm), 分成前收入: this.formatIncome(item.preIncome), 分成后收入: this.formatIncome(item.postIncome)
      }));
      this.downloadCsv(rows, "流量主数据明细");
    },
    exportCompareCsv() {
      const rows = this.comparisonRows.map(item => ({
        数据来源: "抖音官方", 对比项: item.name, 查询状态: item.status, 展示PV: item.displayPv, 点击PV: item.clickPv,
        点击率: this.formatPercent(item.clickRate), 分成前收入: this.formatIncome(item.preIncome),
        分成后收入: this.formatIncome(item.postIncome), ECPM: this.formatIncome(item.ecpm), 收入占比: this.formatPercent(item.share)
      }));
      this.downloadCsv(rows, "流量主数据对比");
    },
    downloadCsv(rows, filename) {
      if (!rows.length) return;
      const headers = Object.keys(rows[0]);
      const escape = value => `"${String(value == null ? "" : value).replace(/"/g, '""')}"`;
      const csv = "\ufeff" + [headers, ...rows.map(row => headers.map(header => row[header]))].map(row => row.map(escape).join(",")).join("\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${filename}-${formatLocalDate(new Date())}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },
    formatInteger(value) {
      return value === null ? "--" : Number(value).toLocaleString("zh-CN");
    },
    formatNumber(value) {
      return Number(value || 0).toFixed(2);
    },
    formatIncome(value) {
      return value === null || value === undefined || value === "" ? "待同步" : this.formatNumber(value);
    },
    formatRealtimeCost(value) {
      if (value === null || value === undefined || value === "") return "0.00000";
      const number = Number(value);
      return Number.isFinite(number) ? number.toFixed(5) : "0.00000";
    },
    formatPercent(value) {
      if (value === null || value === undefined) return "--";
      return `${(Number(value || 0) * 100).toFixed(2)}%`;
    },
    quit() {
      localStorage.removeItem("token");
      localStorage.removeItem("role");
      this.$router.push("/login");
    }
  },
  mounted() {
    this.fetchGames();
    this.sidebarRefreshTimer = window.setInterval(() => { if (!this.isLoading) this.loadSidebarSummaries(); }, 15000);
    this.realtimeTimer = window.setInterval(() => { if (this.realtimeData && !this.realtimeLoading) this.queryMiniAppRealtime(false); }, 60000);
  },
  beforeDestroy() {
    this.queryVersion++;
    window.clearInterval(this.sidebarRefreshTimer);
    window.clearInterval(this.realtimeTimer);
  }
};
</script>

<style scoped>
.traffic-page { min-height: 100vh; color: #dbe8f7; background: #0d172b; }
.traffic-header { height: 76px; padding: 0 28px; display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #1b2d48; background: #0c1629; }
.traffic-brand, .traffic-user { display: flex; align-items: center; gap: 18px; }
.traffic-back { color: #9fb6d1; font-size: 17px; padding: 11px 18px; border: 1px solid #1c3453; border-radius: 10px; }
.traffic-tab { color: #1ed7ee; font-size: 20px; font-weight: 700; padding: 12px 22px; border: 2px solid #10c6e2; border-radius: 12px; background: #102b43; }
.traffic-user { color: #2bd9ef; font-size: 16px; font-weight: 650; }
.traffic-version { color: #899bb7; font-weight: 400; }
.traffic-content { padding: 38px 28px 80px; }
.traffic-title-row { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
.traffic-kicker { color: #2fd9ee; font-size: 14px; letter-spacing: 2px; }
.traffic-title-row h1 { margin: 7px 0 8px; color: #eff7ff; font-size: 32px; }
.traffic-title-row p, .summary-note, .compare-description { color: #778ba8; font-size: 16px; margin: 0; }
.traffic-title-row .el-button { color: #89a2bf; }
.traffic-layout { display: grid; grid-template-columns: 382px minmax(0, 1fr); gap: 28px; align-items: start; }
.game-sidebar, .filter-panel, .data-panel { border: 1px solid #1b3351; border-radius: 16px; background: #0b1a31; box-shadow: 0 12px 28px rgba(0,0,0,.12); }
.game-sidebar { padding: 22px 16px 24px; min-height: 350px; }
.game-search-input :deep(.el-input__inner), .filter-panel :deep(.el-input__inner), .filter-panel :deep(.el-range-input) { color: #dfeaf8; background: #091425; border-color: #2a4260; }
.game-search-input :deep(.el-input__inner) { height: 50px; border: 2px solid #12c8e5; border-radius: 12px; font-size: 16px; }
.game-group { margin-top: 22px; }
.game-group-title { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 12px; padding: 0 3px 10px; border-bottom: 1px solid #1d3551; }
.game-group-account { display: flex; align-items: center; gap: 9px; min-width: 0; }
.game-group-avatar { width: 30px; height: 30px; display: inline-flex; align-items: center; justify-content: center; flex: 0 0 30px; color: #fff; background: #2f70c9; border-radius: 8px; font-size: 14px; font-weight: 700; }
.game-group-account strong { display: block; overflow: hidden; color: #deebfa; font-size: 15px; text-overflow: ellipsis; white-space: nowrap; }
.game-group-account small { display: block; margin-top: 3px; color: #8a9db6; font-size: 11px; }
.game-group-hint { color: #7389a5; font-size: 10px; white-space: nowrap; }
.sidebar-game-card { min-width: 0; margin-bottom: 12px; padding: 14px 14px 11px; cursor: pointer; background: #10243c; border: 1px solid #203f5d; border-radius: 10px; box-shadow: 0 5px 14px rgba(0,0,0,.12); transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease; }
.sidebar-game-card:hover, .sidebar-game-card.active { border-color: #1dcce5; box-shadow: 0 7px 20px rgba(22,190,220,.15); transform: translateY(-1px); }
.sidebar-game-card.active { background: #12324d; }
.sidebar-game-head { display: flex; align-items: center; min-width: 0; gap: 10px; padding-bottom: 12px; }
.sidebar-game-icon { position: relative; flex: 0 0 42px; width: 42px; height: 42px; overflow: hidden; background: #101a2c; border-radius: 9px; }
.sidebar-game-icon img { width: 151px; height: 42px; max-width: none; object-fit: contain; object-position: left center; }
.sidebar-game-title { min-width: 0; flex: 1; }
.sidebar-game-title strong { display: block; overflow: hidden; color: #edf5ff; font-size: 14px; line-height: 1.35; text-overflow: ellipsis; white-space: nowrap; }
.sidebar-game-title small { display: block; margin-top: 4px; color: #8398b2; font-size: 10px; }
.sidebar-game-arrow { flex: 0 0 auto; color: #94a9be; font-size: 16px; }
.sidebar-metric-grid { display: grid; grid-template-columns: repeat(2, 1fr); border-top: 1px solid #1d3852; border-bottom: 1px solid #1d3852; }
.sidebar-metric-grid > div { min-width: 0; padding: 9px 7px 8px 0; }
.sidebar-metric-grid > div:nth-child(2n) { padding-left: 10px; border-left: 1px solid #1d3852; }
.sidebar-metric-grid > div:nth-child(n + 3) { border-top: 1px solid #1d3852; }
.sidebar-metric-grid span { display: block; margin-bottom: 4px; overflow: hidden; color: #8ba2bb; font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.sidebar-metric-grid strong { display: block; overflow: hidden; color: #54c8e4; font-size: 15px; line-height: 1.1; text-overflow: ellipsis; white-space: nowrap; }
.sidebar-metric-grid .sidebar-pre-income { color: #e9b34d; }
.sidebar-metric-grid .sidebar-post-metric { grid-column: 1 / -1; padding-left: 0; border-left: 0; }
.sidebar-metric-grid .sidebar-post-metric strong { color: #5bd0ad; }
.sidebar-game-foot { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding-top: 10px; color: #71ae98; font-size: 10px; }
.sidebar-game-foot b { color: #65afd2; font-size: 10px; font-weight: 600; white-space: nowrap; }
.sidebar-game-foot i { margin-right: 3px; }
.sidebar-empty { margin-top: 28px; color: #7489a7; font-size: 14px; }
.traffic-main { min-width: 0; }
.filter-panel { padding: 24px; display: flex; flex-wrap: wrap; gap: 18px; align-items: flex-end; }
.filter-item { min-width: 180px; flex: 1 1 180px; }
.filter-date { flex: 1.45 1 300px; }
.filter-os { flex-basis: 180px; }
.filter-item label, .filter-compare label { display: block; color: #80a6ca; margin: 0 0 8px; font-size: 15px; }
.filter-item .el-select, .filter-item .el-date-editor { width: 100%; }
.filter-panel :deep(.el-input__inner), .filter-panel :deep(.el-range-editor) { height: 48px; border-radius: 11px; }
.filter-panel :deep(.el-range-editor) { background: #091425; border-color: #2a4260; }
.filter-actions { display: flex; gap: 12px; align-items: flex-end; }
.filter-actions .el-button, .filter-compare .el-button { height: 48px; border-radius: 11px; font-size: 16px; }
.filter-actions .el-button--primary { color: #06202c; background: #19c9e2; border-color: #19c9e2; }
.filter-actions .el-button:not(.el-button--primary), .filter-compare .el-button { color: #b4c9df; background: #10283f; border-color: #1e3d5c; }
.filter-compare { display: flex; align-items: flex-end; gap: 10px; margin-left: auto; }
.filter-compare label { margin-bottom: 16px; }
.filter-compare .el-select { width: 140px; }
.traffic-warning { margin: 14px 0; padding: 10px 14px; color: #f5ca73; background: #2a2415; border: 1px solid #735d2b; border-radius: 8px; }
.traffic-warning i { margin-right: 8px; }
.summary-grid { display: grid; grid-template-columns: repeat(5, minmax(135px, 1fr)); gap: 18px; margin: 28px 0 14px; }
.summary-card { min-height: 106px; padding: 20px 22px; border: 1px solid #14557a; border-left: 5px solid #1ac5e2; border-radius: 12px; background: linear-gradient(130deg, #0e304c, #0b2038); }
.summary-card span { display: block; color: #8ba9c8; font-size: 17px; }
.summary-card strong { display: block; margin-top: 8px; color: #4be6d2; font-size: 29px; letter-spacing: 1px; }
.summary-note { margin: 0 0 24px; font-size: 14px; }
.realtime-cards { display: grid; grid-template-columns: repeat(4, minmax(150px, 1fr)); gap: 12px; margin-bottom: 14px; }
.realtime-card { padding: 15px 17px; border: 1px solid #245574; border-radius: 10px; background: #102840; }
.realtime-card span { display: block; color: #86a4c1; font-size: 13px; }
.realtime-card strong { display: block; overflow: hidden; margin-top: 8px; color: #51e0ce; font-size: 20px; text-overflow: ellipsis; white-space: nowrap; }
.realtime-panel .summary-note { margin-bottom: 16px; }
.data-panel { margin-top: 20px; padding: 24px 28px 28px; }
.panel-title { color: #22d6ed; font-size: 21px; font-weight: 700; margin-bottom: 18px; }
.panel-title i { display: inline-block; width: 5px; height: 23px; margin-right: 10px; vertical-align: -3px; border-radius: 3px; background: #1cd5e9; }
.panel-title span { color: #7d96b4; font-size: 13px; font-weight: 400; margin-left: 8px; }
.trend-chart { height: 220px; padding: 15px 16px 0; display: flex; align-items: stretch; gap: 12px; border-bottom: 1px solid #1a2c45; }
.trend-column { flex: 1; min-width: 34px; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; color: #7e94b2; font-size: 12px; }
.trend-value { min-height: 18px; color: #61dccc; font-size: 11px; transform: rotate(-45deg); white-space: nowrap; }
.trend-bar-wrap { height: 145px; width: 100%; display: flex; align-items: flex-end; justify-content: center; }
.trend-bar { width: min(34px, 75%); min-height: 8px; border-radius: 5px 5px 0 0; background: linear-gradient(#28d6e6, #157aa2); }
.trend-column > span { padding-top: 9px; }
.panel-empty { padding: 58px 0; color: #758ba8; text-align: center; font-size: 16px; }
.traffic-table :deep(.el-table), .traffic-table :deep(.el-table__expanded-cell) { color: #c8d8ea; background: transparent; }
.traffic-table :deep(.el-table th), .traffic-table :deep(.el-table tr), .traffic-table :deep(.el-table td) { color: #c8d8ea; background: #0b1a31; border-color: #203650; }
.traffic-table :deep(.el-table th) { color: #27d7eb; background: #081426; font-size: 14px; }
.traffic-table :deep(.el-table--striped .el-table__body tr.el-table__row--striped td) { background: #0d2038; }
.traffic-table :deep(.el-table__body tr:hover > td) { background: #12304d !important; }
.compare-description { margin-bottom: 14px; line-height: 1.6; }
.compare-panel > .el-button { color: #a9c3db; background: #10283f; border-color: #1e3d5c; }
.compare-table { margin-top: 18px; }
@media (max-width: 1180px) { .traffic-layout { grid-template-columns: 320px minmax(0, 1fr); } .summary-grid { grid-template-columns: repeat(3, minmax(145px, 1fr)); } .realtime-cards { grid-template-columns: repeat(2, minmax(145px, 1fr)); } .filter-compare { margin-left: 0; } .game-group-hint { display: none; } }
@media (max-width: 760px) { .traffic-header { height: auto; padding: 14px 16px; align-items: flex-start; } .traffic-header, .traffic-brand, .traffic-user { flex-wrap: wrap; } .traffic-user { gap: 8px; font-size: 13px; } .traffic-content { padding: 24px 14px 50px; } .traffic-title-row h1 { font-size: 26px; } .traffic-layout { display: block; } .game-sidebar { margin-bottom: 18px; } .filter-panel { padding: 16px; } .filter-actions, .filter-compare { width: 100%; } .filter-actions .el-button, .filter-compare .el-select { flex: 1; } .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; } .summary-card { min-height: 86px; padding: 14px; } .summary-card span { font-size: 13px; } .summary-card strong { font-size: 21px; } .data-panel { padding: 18px 12px; } }
</style>
